import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Crown,
  ExternalLink,
  LayoutDashboard,
  Package,
  RotateCcw,
  User,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

interface CreatedGroupData {
  id: number;
  name: string;
  groupe_type: string;
  licence: number;
  plan_type?: string;
  max_parcours_per_month?: number;
  tier_id?: string;
}

interface CreatedUserData {
  id: number;
  username: string;
  email: string;
  profil_name?: string;
}

interface StepDoneProps {
  createdGroup: CreatedGroupData;
  createdUsers: CreatedUserData[];
  onReset: () => void;
}

export function StepDone({ createdGroup, createdUsers, onReset }: StepDoneProps) {
  const isPremium = createdGroup.groupe_type === "PREMIUM";

  return (
    <div className="space-y-8 text-center">
      {/* Hero success icon */}
      <div className="flex flex-col items-center gap-4 pt-2">
        <div className="relative">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          {/* Decorative rings */}
          <div className="absolute inset-0 rounded-full border-4 border-emerald-200/50 animate-ping [animation-duration:2s] [animation-iteration-count:3]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Tout est prêt !
          </h2>
          <p className="text-muted-foreground text-sm mt-1.5">
            Le groupe et ses utilisateurs ont été créés avec succès.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
        {/* Group card */}
        <div className="rounded-xl border-2 border-border/60 bg-card p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Groupe créé
              </p>
            </div>
            <div className="ml-auto">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </div>
          </div>

          <div className="space-y-2">
            <p className="font-semibold text-base text-foreground truncate">
              {createdGroup.name}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Badge
                variant="secondary"
                className={cn(
                  "text-xs gap-1",
                  isPremium
                    ? "bg-amber-100 text-amber-700 border-amber-200"
                    : "bg-primary/10 text-primary border-primary/20"
                )}
              >
                {isPremium ? (
                  <Crown className="h-3 w-3" />
                ) : (
                  <Package className="h-3 w-3" />
                )}
                {createdGroup.groupe_type}
              </Badge>

              <Badge variant="outline" className="text-xs gap-1">
                <Users className="h-3 w-3" />
                {createdGroup.licence} licence{createdGroup.licence > 1 ? "s" : ""}
              </Badge>

              {isPremium && createdGroup.plan_type && (
                <Badge variant="outline" className="text-xs">
                  Plan {createdGroup.plan_type}
                </Badge>
              )}

              {isPremium && createdGroup.max_parcours_per_month && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  {createdGroup.max_parcours_per_month} parcours/mois
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">ID #{createdGroup.id}</p>
          </div>
        </div>

        {/* Users card */}
        <div className="rounded-xl border-2 border-border/60 bg-card p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Utilisateurs créés
              </p>
            </div>
            <div className="ml-auto">
              <Badge variant="secondary" className="text-xs">
                {createdUsers.length}
              </Badge>
            </div>
          </div>

          {createdUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-2">
              Aucun utilisateur créé — vous pouvez en ajouter depuis la gestion des utilisateurs.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
              {createdUsers.map((user, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2"
                >
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-background border border-border/60 shrink-0">
                    <User className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate text-foreground">
                      {user.username}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {user.email}
                    </p>
                  </div>
                  {user.profil_name && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 shrink-0"
                    >
                      {user.profil_name}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-2 w-full sm:w-auto"
          onClick={onReset}
        >
          <RotateCcw className="h-4 w-4" />
          Créer un autre groupe
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-2 w-full sm:w-auto"
          asChild
        >
          <Link to={`/admin/droits/groupe/${createdGroup.id}`}>
            <ExternalLink className="h-4 w-4" />
            Voir le groupe
          </Link>
        </Button>

        <Button size="sm" className="gap-2 w-full sm:w-auto" asChild>
          <Link to="/admin/dashboard">
            <LayoutDashboard className="h-4 w-4" />
            Tableau de bord
          </Link>
        </Button>
      </div>

      {/* Quick links */}
      {createdUsers.length > 0 && (
        <div className="text-xs text-muted-foreground pt-1">
          Gérez vos utilisateurs depuis{" "}
          <Link
            to="/admin/droits/utilisateur"
            className="text-primary underline-offset-2 hover:underline"
          >
            la liste des utilisateurs
          </Link>
          .
        </div>
      )}
    </div>
  );
}
